import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Modal, errorMessage, inputCls, primaryBtn, secondaryBtn, validateOpcoCode } from './shared'

const DOMAIN_RE = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9-]{2,63}$/
const ADDRESS_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// True for a domain (example.nl, @example.nl) or one address (jan@example.nl).
export function isOpcoEmail(value) {
  const bare = String(value || '').trim().toLowerCase().replace(/^@/, '').replace(/\.$/, '')
  return bare.includes('@') ? ADDRESS_RE.test(bare) : DOMAIN_RE.test(bare)
}

// Add an OpCo (code + name + its email domain or one address) or rename one
// (code is immutable, shown read-only). The email becomes an auto-assign rule
// (switched off) and keeps these people out of other OpCos' invite search;
// more domains or addresses are added later under Auto-assign.
export default function OpCoFormModal({ opco = null, onSubmit, onClose }) {
  const editing = Boolean(opco)
  const [code, setCode] = useState(opco?.code || '')
  const [name, setName] = useState(opco?.name || '')
  const [email, setEmail] = useState('')
  const [touched, setTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [serverError, setServerError] = useState(null)

  const codeError = editing ? null : validateOpcoCode(code)
  const nameError = name.trim() ? null : 'Name is required'
  const emailValue = email.trim().toLowerCase()
  const emailError = editing ? null
    : !emailValue ? 'Email domain is required'
      : isOpcoEmail(emailValue) ? null : 'Enter a domain or an email address'
  const invalid = Boolean(codeError || nameError || emailError)

  const submit = async (e) => {
    e.preventDefault()
    setTouched(true)
    if (invalid) return
    setSaving(true)
    setServerError(null)
    try {
      await onSubmit(editing ? { name: name.trim() } : { code, name: name.trim(), emails: [emailValue] })
    } catch (err) {
      setServerError(errorMessage(err, editing ? 'Could not rename OpCo' : 'Could not create OpCo'))
      setSaving(false)
    }
  }

  return (
    <Modal
      title={editing ? `Rename ${opco.code}` : 'Add OpCo'}
      onClose={saving ? undefined : onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} disabled={saving} className={secondaryBtn}>Cancel</button>
          <button type="submit" form="opco-form" disabled={saving || (touched && invalid)} className={primaryBtn}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {editing ? 'Save' : 'Create OpCo'}
          </button>
        </>
      )}
    >
      <form id="opco-form" onSubmit={submit} className="space-y-4" noValidate>
        <div>
          <label htmlFor="opco-code" className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
            Code
          </label>
          <input
            id="opco-code"
            value={code}
            disabled={editing}
            onChange={(e) => setCode(e.target.value.toLowerCase().trim())}
            placeholder="e.g. nl01"
            maxLength={32}
            autoFocus={!editing}
            className={`${inputCls} font-mono disabled:opacity-60`}
            aria-invalid={Boolean(touched && codeError)}
          />
          {editing ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">The code cannot be changed.</p>
          ) : touched && codeError ? (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{codeError}</p>
          ) : (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Lowercase letters, digits and hyphens. Used in the URL and cannot be changed later.</p>
          )}
        </div>
        <div>
          <label htmlFor="opco-name" className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
            Name
          </label>
          <input
            id="opco-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Example Netherlands"
            maxLength={200}
            autoFocus={editing}
            className={inputCls}
            aria-invalid={Boolean(touched && nameError)}
          />
          {touched && nameError && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{nameError}</p>}
        </div>
        {!editing && (
          <div>
            <label htmlFor="opco-email" className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              Email domain
            </label>
            <input
              id="opco-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example.nl or jan@example.nl"
              maxLength={320}
              className={`${inputCls} font-mono`}
              aria-invalid={Boolean(touched && emailError)}
            />
            {touched && emailError && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{emailError}</p>}
          </div>
        )}
        {serverError && (
          <p role="alert" className="rounded-lg bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">{serverError}</p>
        )}
      </form>
    </Modal>
  )
}
